import { useEffect, useMemo } from "react";

import { LabelAndSelect, type OptionItem } from "../reuseables/form.comp";

import { useTranslation } from "../../core/translation.core";
import useRetailShop from "../../hooks/useRetailShop";
import { Warehouse } from "../../types/warehouse";
import {
  useDestRetailWarehouses,
  usePosWarehouseList,
  useRetailShopsForTransfer,
  type WarehouseTransferDetailFormValues,
  type WarehouseTransferFormMethods,
} from "../../hooks/warehouseTransfer/transferForm.shared";
import { useWarehouseTransferNoteDetail } from "../../hooks/warehouseTransfer/warehouseTransferNoteDetail.hooks";

export function WarehouseTransferFromWarehouseSelect({ methods }: { methods: WarehouseTransferFormMethods }) {
  const { t, msg } = useTranslation();
  const { note, isOutgoing } = useWarehouseTransferNoteDetail();
  const { data: warehouses } = usePosWarehouseList();

  const fromOptions = useMemo(() => {
    if (!warehouses?.length || !note || !isOutgoing) return [] as Warehouse[];
    return warehouses;
  }, [warehouses, note, isOutgoing]);

  const fromSelectOptions: OptionItem[] = useMemo(() => fromOptions.map((w) => ({ label: w.name, value: w.id })), [fromOptions]);

  return (
    <LabelAndSelect<WarehouseTransferDetailFormValues>
      methods={methods}
      label={t(msg.warehouseTransfer.fromWarehouse)}
      fieldName="fromWarehouseId"
      options={fromSelectOptions}
      setDefault={false}
      topLabelCss="block w-full"
      selectCss="w-full"
    />
  );
}

/** Create flow: all shop warehouses exposed by POS list (eShop + retail per shop flags). */
export function NewWarehouseTransferFromWarehouseSelect({
  methods,
  emptyOptionLabel,
}: {
  methods: WarehouseTransferFormMethods;
  emptyOptionLabel: string;
}) {
  const { t, msg } = useTranslation();
  const currentRetail = useRetailShop();
  const { data: warehouses } = usePosWarehouseList();

  const fromOptions = useMemo(() => warehouses ?? ([] as Warehouse[]), [warehouses]);

  const fromSelectOptions: OptionItem[] = useMemo(() => {
    const rows = fromOptions.map((w) => ({ label: w.name, value: w.id }));
    return [{ label: emptyOptionLabel, value: "" }, ...rows];
  }, [fromOptions, emptyOptionLabel]);

  useEffect(() => {
    const cur = methods.getValues("fromWarehouseId");
    if (cur) return;
    if (!fromOptions.length) return;

    const ew = currentRetail?.eshopWarehouseId;
    if (ew) {
      const match = fromOptions.find((w) => String(w.id) === String(ew));
      if (match) {
        methods.setValue("fromWarehouseId", match.id, { shouldValidate: true });
        return;
      }
    }

    if (fromOptions.length === 1) {
      methods.setValue("fromWarehouseId", fromOptions[0].id, { shouldValidate: true });
    }
  }, [fromOptions, methods, currentRetail?.eshopWarehouseId]);

  return (
    <LabelAndSelect<WarehouseTransferDetailFormValues>
      methods={methods}
      label={t(msg.warehouseTransfer.fromWarehouse)}
      fieldName="fromWarehouseId"
      options={fromSelectOptions}
      setDefault={false}
      topLabelCss="block w-full"
      selectCss="w-full"
    />
  );
}

export function WarehouseTransferToRetailShopSelect({
  methods,
  emptyOptionLabel,
  defaultRetailShopId,
}: {
  methods: WarehouseTransferFormMethods;
  emptyOptionLabel?: string;
  /** When set and the field is still empty, selects this retail if it appears in the transfer store list (e.g. current POS store on create). */
  defaultRetailShopId?: string;
}) {
  const { t, msg } = useTranslation();
  const { data: allRetails } = useRetailShopsForTransfer();
  const toRetailSelectOptions: OptionItem[] = useMemo(() => {
    const rows = (allRetails ?? []).map((r) => ({ label: r.name, value: r.id }));
    return emptyOptionLabel ? [{ label: emptyOptionLabel, value: "" }, ...rows] : rows;
  }, [allRetails, emptyOptionLabel]);

  useEffect(() => {
    if (!defaultRetailShopId || !allRetails?.length) return;
    if (methods.getValues("toRetailShopId")) return;
    if (!allRetails.some((r) => r.id === defaultRetailShopId)) return;
    methods.setValue("toRetailShopId", defaultRetailShopId, { shouldValidate: true });
  }, [defaultRetailShopId, allRetails, methods]);

  return (
    <LabelAndSelect<WarehouseTransferDetailFormValues>
      methods={methods}
      label={t(msg.warehouseTransfer.toStore)}
      fieldName="toRetailShopId"
      options={toRetailSelectOptions}
      setDefault={false}
      topLabelCss="block w-full"
      selectCss="w-full"
    />
  );
}

export function WarehouseTransferToWarehouseSelect({
  showLineEditor,
  methods,
  emptyOptionLabel,
  lockUntilRetailSelected,
}: {
  showLineEditor: boolean;
  methods: WarehouseTransferFormMethods;
  emptyOptionLabel?: string;
  lockUntilRetailSelected?: boolean;
}) {
  const { t, msg } = useTranslation();
  const toRetailShopIdWatched = methods.watch("toRetailShopId");
  const { data: warehouses } = usePosWarehouseList();
  const { data: destRetailWarehouses } = useDestRetailWarehouses(toRetailShopIdWatched);

  const toWarehouseOptions = useMemo(() => {
    if (!warehouses?.length || !destRetailWarehouses?.warehouseIds?.length) return [] as Warehouse[];
    const allowed = new Set(destRetailWarehouses.warehouseIds.map((x: string) => String(x)));
    return warehouses.filter((w) => allowed.has(w.id));
  }, [warehouses, destRetailWarehouses]);

  const toWarehouseSelectOptions: OptionItem[] = useMemo(() => {
    const rows = toWarehouseOptions.map((w) => ({ label: w.name, value: w.id }));
    return emptyOptionLabel ? [{ label: emptyOptionLabel, value: "" }, ...rows] : rows;
  }, [toWarehouseOptions, emptyOptionLabel]);

  useEffect(() => {
    if (!showLineEditor) return;
    if (!toWarehouseOptions.length) return;
    const cur = methods.getValues("toWarehouseId");
    if (emptyOptionLabel) {
      if (!cur) {
        methods.setValue("toWarehouseId", toWarehouseOptions[0].id, { shouldValidate: true });
      } else if (!toWarehouseOptions.some((w) => w.id === cur)) {
        methods.setValue("toWarehouseId", toWarehouseOptions[0].id, { shouldValidate: true });
      }
    } else if (!toWarehouseOptions.some((w) => w.id === cur)) {
      methods.setValue("toWarehouseId", toWarehouseOptions[0].id, { shouldValidate: true });
    }
  }, [showLineEditor, toWarehouseOptions, methods, emptyOptionLabel]);

  const select = (
    <LabelAndSelect<WarehouseTransferDetailFormValues>
      methods={methods}
      label={t(msg.warehouseTransfer.toWarehouse)}
      fieldName="toWarehouseId"
      options={toWarehouseSelectOptions}
      setDefault={false}
      topLabelCss="block w-full"
      selectCss="w-full"
    />
  );

  if (lockUntilRetailSelected && !toRetailShopIdWatched) {
    return (
      <fieldset disabled className="min-w-0 border-0 p-0 m-0 disabled:opacity-60">
        {select}
      </fieldset>
    );
  }

  return select;
}
